import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { directDebitApi } from "../services/directDebitApi";
import { ErrorState, LoadingState, PrimaryButton } from "../../paymentLinking/components";
import BankPicker from "../components/BankPicker";
import BindingLoadingOverlay from "../components/BindingLoadingOverlay";
import BindingSuccess from "../components/BindingSuccess";
import CardEntry from "../components/CardEntry";
import OtpModal from "../components/OtpModal";

const SUCCESS_REDIRECT_DELAY_MS = 3000;

/**
 * Yokke / Mandiri Direct Debit hosted binding (Partial SNAP + Option B).
 *
 * Registration completes after OTP verify (Set Token Limit is optional and
 * not part of this UI). Route: /b2b/direct-debit/bind/:publicToken
 */
const MandiriBinding = () => {
  const { publicToken } = useParams();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);

  const pollRef = useRef(null);

  const loadState = useCallback(async () => {
    const data = await directDebitApi.getState(publicToken);
    setState(data);
    return data;
  }, [publicToken]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await loadState();
        if (!cancelled) {
          setState(data);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Failed to load binding session");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadState, publicToken]);

  useEffect(() => {
    if (!state || state.phase !== "card_submitted") {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }

    pollRef.current = setInterval(async () => {
      try {
        const data = await loadState();
        if (data.phase === "awaiting_otp" || data.status === "FAILED" || data.status === "EXPIRED") {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch {
        // keep polling
      }
    }, 2500);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [state, loadState]);

  useEffect(() => {
    if (state?.phase === "redirect_external" && state.redirect_url) {
      window.location.href = state.redirect_url;
      return;
    }
    if (state?.status !== "ACTIVE" || !state.success_redirect_url) {
      return undefined;
    }
    const url = state.success_redirect_url;
    const timer = window.setTimeout(() => {
      window.location.href = url;
    }, SUCCESS_REDIRECT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [state]);

  const handleSelectBank = async (bankCode) => {
    setSubmitting(true);
    setError(null);
    try {
      const data = await directDebitApi.selectBank(publicToken, bankCode);
      setState(data);
    } catch (err) {
      setError(err.message || "Failed to select bank");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitCard = async (cardNumber, expiry) => {
    setSubmitting(true);
    setError(null);
    try {
      const data = await directDebitApi.submitCard(publicToken, cardNumber, expiry);
      setState(data);
    } catch (err) {
      setError(err.message || "Card validation failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    setVerifyingOtp(true);
    setSubmitting(true);
    setError(null);
    try {
      const data = await directDebitApi.verifyOtp(publicToken, otp);
      setState(data);
      return true;
    } catch (err) {
      setError(err.message || "OTP verification failed");
      return false;
    } finally {
      setSubmitting(false);
      setVerifyingOtp(false);
    }
  };

  const handleResendOtp = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const data = await directDebitApi.resendOtp(publicToken);
      setState(data);
      return true;
    } catch (err) {
      setError(err.message || "Failed to resend OTP");
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingState message="Memuat sesi binding..." />;
  }

  if (error && !state) {
    return <ErrorState message={error} />;
  }

  if (state?.status === "FAILED" || state?.status === "EXPIRED") {
    const redirect = state.failure_redirect_url;
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-xl shadow p-6 text-center space-y-4">
          <h1 className="text-lg font-semibold text-gray-900">Binding gagal</h1>
          <p className="text-sm text-gray-600">{state.failure_reason || "Sesi binding tidak dapat dilanjutkan."}</p>
          {redirect && (
            <PrimaryButton onClick={() => { window.location.href = redirect; }}>
              Kembali ke merchant
            </PrimaryButton>
          )}
        </div>
      </div>
    );
  }

  if (state?.status === "ACTIVE") {
    return (
      <BindingSuccess
        maskedCard={state.masked_card}
        brandName={state.brand_name}
        logoUrl={state.logo_url}
      />
    );
  }

  const phase = state?.phase || "awaiting_card";
  const cardStep = phase === "awaiting_card"
    || phase === "awaiting_otp"
    || phase === "card_submitted";

  if (phase === "choose_bank") {
    return (
      <BankPicker
        banks={state.banks || []}
        submitting={submitting}
        error={error}
        onSelect={handleSelectBank}
        brandName={state.brand_name}
        logoUrl={state.logo_url}
      />
    );
  }

  if (cardStep) {
    const showOverlay = phase === "card_submitted"
      || (phase === "awaiting_card" && submitting)
      || verifyingOtp;

    return (
      <>
        <div inert={showOverlay || phase === "awaiting_otp" ? true : undefined}>
          <CardEntry
            submitting={submitting}
            error={phase === "awaiting_card" && !showOverlay ? error : null}
            onSubmit={handleSubmitCard}
            brandName={state.brand_name}
            logoUrl={state.logo_url}
          />
        </div>
        {phase === "awaiting_otp" ? (
          <OtpModal
            hidden={verifyingOtp}
            maskedMobile={state.masked_mobile}
            submitting={submitting}
            error={error}
            onVerify={handleVerifyOtp}
            onResend={handleResendOtp}
          />
        ) : null}
        {showOverlay ? <BindingLoadingOverlay /> : null}
      </>
    );
  }

  return null;
};

export default MandiriBinding;
